import { useEffect, useState } from 'react'

import { get } from 'lodash'
import { useTable, useSortBy } from 'react-table'

import styled from 'styled-components'
import LeaderBoard from './LeaderBoard'
import { tertiary } from '../variables'

import GAEvent from '../modules/analytics-event-helper'
import isImageUrl from '../modules/is-image-url'

import { GA } from '../constants'

import ImageModal from './ImageModal'

import usePostResults from '../hooks/usePostResults'
import usePostUserScore from '../hooks/usePostUserScore'

const columns = [{
  Header: 'Name',
  accessor: 'name',
  Cell: row => <div><span title={row.value}>{isImageUrl(row.value) && <ImageModal alt={row.value} src={row.value} height={100} />}{!isImageUrl(row.value) && <>{row.value}</>}</span></div>,
  defaultCanSort: false
}, {
  Header: 'Popular Rank',
  Cell: (row) => {
    return <div>{row.value}</div>
  },
  accessor: 'popRank',
  maxWidth: 126,
  sortType: 'number'
}, {
  id: 'yourRank',
  Header: 'Your Rank',
  accessor: d => d.yourRank === 'N/A' ? 'N/A' : d.yourRank + 1,
  maxWidth: 108,
  sortType: 'number'
}, {
  id: 'avg',
  Header: 'Avg Rank',
  accessor: d => `${d.avg}`,
  maxWidth: 112,
  sortType: 'number'
}, {
  id: 'mode',
  Header: 'Rank Mode',
  accessor: d => `${d.mode}`,
  maxWidth: 112,
  sortType: 'number'
}, {
  Header: 'No.1 Votes',
  accessor: 'no1s',
  maxWidth: 102,
  sortType: 'number'
}, {
  Header: 'Sortada Score™',
  accessor: 'score',
  maxWidth: 102,
  sortType: 'number'
}]

const ResultsBox = styled.div`
  box-sizing: border-box;
  overflow: hidden;
`

const StyledReactTable = styled.table`
  border: 0;
  width: 100%;
  td {
    text-align: right;

    &:first-child {
      text-align: left;
    }
  }
  thead {
    box-shadow: none !important;
    border: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.2) !important;

    th {
      padding: 1em;
      text-align: left;
      font-weight: bold;
      text-transform: uppercase;
      font-size: 0.8em;
      text-align: right;

      &:first-child {
        text-align: left;
      }

      &:focus {
        outline: none;
      }
    }
  }

  tbody {
    td {
      padding: 1em;
      border: 0;
    }
  }
`

const VictorsBox = styled.div`
  display: block;
  margin: 0 0 0 0;
  color: white;
  background-color: ${tertiary};
  padding: 0.5em 1.5em 0.5em 1.5em;
  font-size: 1.1em;
`

const YourScoreBox = styled.div`
  margin: 0 0 1em 0;
  padding: 1em;
  background-color: #f5f5f5;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ScoreSection = styled.div``

const VoteSection = styled.div`
  font-style: italic;
  text-transform: uppercase;
  font-size: 0.85em;
  color: #666;
`

const YourScore = styled.span`
  color: ${({ you, total }) => you / total > 0.70 ? 'green' : 'red'};
  font-weight: 800;
  font-size: 1.4em;
`

const LabelText = styled.span`
  font-style: italic;
  text-transform: uppercase;
  font-size: 0.85em;
  margin-right: 0.5em;
  color: #666;
`

const ResultsTable = ({ columns, data }) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({ columns, data }, useSortBy)
  return (
    <StyledReactTable {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup, idx) => (
          <tr key={`header-group-tr-${idx}`} {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, colIdx) => (
              <th key={`header-group-th-${colIdx}`} {...column.getSortByToggleProps()} {...column.getHeaderProps()}>{column.render('Header')}</th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row)
          return (
            <tr key={`body-row-${i}`} {...row.getRowProps()}>
              {row.cells.map((cell, cellIdx) => {
                return <td key={`body-cell-${cellIdx}`} {...cell.getCellProps()}>{cell.render('Cell')}</td>
              })}
            </tr>
          )
        })}
      </tbody>
    </StyledReactTable>
  )
}

const Results = ({ postulation, vote, isQuick = false }) => {
  const [voteCount, setVoteCount] = useState(null)

  if (!vote && !postulation.isClosed && !postulation.isExpired) {
    return null
  }

  const { results, tally, loading: resultsLoading } = usePostResults({ post: postulation, vote })
  const { score, loading: scoreLoading } = usePostUserScore({ postId: postulation.id, isQuick })

  useEffect(() => {
    GAEvent({
      category: postulation.isQuick ? GA.CATEGORY.QUICK : GA.CATEGORY.POST,
      action: GA.ACTION.VIEW_RESULTS,
      label: postulation.id
    })
  }, [postulation.id])

  if (resultsLoading || scoreLoading) {
    return <div>Loading...</div>
  }

  const count = Object.keys(get(results, 'totals', [])).length
  const scoreMax = (count * (count + 1)) / 2

  return (
    <ResultsBox>
      {results && results.winners &&
        <VictorsBox>
          {(results && results.winners) ? <LeaderBoard postId={postulation.id} onVoteCount={setVoteCount} /> : null}
        </VictorsBox>}
      {(score || voteCount) && (
        <YourScoreBox>
          <ScoreSection>
            {score && (
              <>
                <LabelText>Your score:</LabelText>
                <YourScore you={score} total={scoreMax}>{score}</YourScore>
                <span style={{ fontSize: '1.1em', marginLeft: '0.2em', color: '#666' }}>/{scoreMax}</span>
              </>
            )}
          </ScoreSection>
          <VoteSection>
            {voteCount && `Votes: ${voteCount}`}
          </VoteSection>
        </YourScoreBox>
      )}
      <div style={{ overflowX: 'auto' }}>
        {tally && <ResultsTable data={tally} columns={columns} />}
      </div>
    </ResultsBox>
  )
}

export default Results
